import { readLoadedDeploymentAssetVersion } from "../lib/deploymentUpdateRuntime.js";

const safe = (read, fallback = null) => { try { return read(); } catch { return fallback; } };
const identifier = (value) => typeof value === "string" && /^[A-Za-z0-9_.:-]{1,128}$/.test(value) ? value : null;
const duration = (value) => Number.isFinite(value) && value >= 0 ? Math.min(Math.round(value * 10) / 10, 86400000) : null;
const HEADERS = {
  request_id: "x-request-id", correlation_id: "x-correlation-id", railway_request_id: "x-railway-request-id",
  railway_edge: "x-railway-edge", hikari_trace: "x-hikari-trace", server: "server",
};
const ENUMS = {
  phase: ["created", "request", "read_body", "parsed", "invalid_json", "body_error", "network_error"],
  content_kind: ["json", "html", "text", "other", "missing"],
  error_name: ["Error", "TypeError", "SyntaxError", "AbortError", "TimeoutError", "NetworkError", "other"],
  visibility: ["visible", "hidden", "prerender"],
};

// Shared by the screenshot context and operation feedback. Never retain bodies,
// URLs, arbitrary headers, credentials or raw exception text.
export function normalizeMutationTransport(value) {
  if (!value || value.schema_version !== 1) return null;
  const result = { schema_version: 1 };
  for (const key of ["loaded_asset_version", "body_request_id", ...Object.keys(HEADERS).filter((key) => key !== "server")]) {
    if (identifier(value[key])) result[key] = value[key];
  }
  if (typeof value.server === "string" && /^[A-Za-z0-9_.:/() -]{1,128}$/.test(value.server)) result.server = value.server;
  for (const [key, allowed] of Object.entries(ENUMS)) {
    if (allowed.includes(value[key])) result[key] = value[key];
  }
  for (const key of ["online", "service_worker_controlled", "response_received", "redirected"]) {
    if (typeof value[key] === "boolean") result[key] = value[key];
  }
  for (const key of ["headers_ms", "body_ms", "elapsed_ms"]) {
    if (duration(value[key]) !== null) result[key] = duration(value[key]);
  }
  if (Number.isInteger(value.http_status) && value.http_status >= 0 && value.http_status <= 599) result.http_status = value.http_status;
  for (const key of ["content_length", "transfer_size", "encoded_body_size", "decoded_body_size"]) {
    if (Number.isSafeInteger(value[key]) && value[key] >= 0) result[key] = value[key];
  }
  if (typeof value.next_hop_protocol === "string" && /^[a-z0-9./-]{1,16}$/.test(value.next_hop_protocol)) result.next_hop_protocol = value.next_hop_protocol;
  if (typeof value.started_at === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value.started_at)) result.started_at = value.started_at;
  return result;
}

export function normalizeServerResponseTransport(value) {
  if (!value || value.schema_version !== 1) return null;
  const result = { schema_version: 1 };
  for (const key of ["request_id", "railway_request_id", "railway_deployment_id", "railway_git_commit_sha", "railway_replica_id"]) {
    if (identifier(value[key])) result[key] = value[key];
  }
  for (const key of ["started_at", "completed_at"]) {
    if (typeof value[key] === "string" && /^\d{4}-\d{2}-\d{2}T[0-9:.+Z-]{8,32}$/.test(value[key])) result[key] = value[key];
  }
  for (const key of ["response_started", "body_complete", "disconnect_observed"]) {
    if (typeof value[key] === "boolean") result[key] = value[key];
  }
  for (const key of ["headers_ms", "body_completed_ms", "duration_ms"]) {
    if (duration(value[key]) !== null) result[key] = duration(value[key]);
  }
  for (const key of ["bytes_sent", "status_code"]) {
    if (Number.isSafeInteger(value[key]) && value[key] >= 0) result[key] = value[key];
  }
  if (value.method === "POST") result.method = value.method;
  if (typeof value.route === "string" && /^\/[A-Za-z0-9_{}./-]{1,255}$/.test(value.route)) result.route = value.route;
  if (["incomplete", "complete", "cancelled", "exception"].includes(value.outcome)) result.outcome = value.outcome;
  if (["gzip", "br", "identity", "", "other"].includes(value.content_encoding)) result.content_encoding = value.content_encoding;
  if (["application", "connection"].includes(value.error_kind)) result.error_kind = value.error_kind;
  return result;
}

// Screenshot metadata also retains the existing import diagnostic after its
// separate activity-history entry expires. Copy only the documented fields.
export function normalizeCapturedMutationDiagnostic(value) {
  if (!value || !["created", "request", "parse_response", "install_result", "publication_effects", "completion", "completed"].includes(value.phase)) return null;
  const result = { phase: value.phase };
  if (identifier(value.loaded_asset_version)) result.loaded_asset_version = value.loaded_asset_version;
  for (const key of ["response_received", "response_ok", "response_accepted"]) {
    if (typeof value[key] === "boolean") result[key] = value[key];
  }
  if (Number.isInteger(value.response_status) && value.response_status >= 0 && value.response_status <= 599) result.response_status = value.response_status;
  if (["basic", "cors", "default", "error", "opaque", "opaqueredirect", "other"].includes(value.response_type)) result.response_type = value.response_type;
  if (["not_read", "reading", "parsed", "invalid"].includes(value.json_state)) result.json_state = value.json_state;
  for (const key of ["error", "json_error"]) {
    const error = value[key];
    if (!error) continue;
    const evidence = { stack_locations: [] };
    if (["object", "string", "number", "boolean", "undefined", "bigint", "symbol", "function", "null"].includes(error.value_type)) evidence.value_type = error.value_type;
    if (typeof error.is_error_instance === "boolean") evidence.is_error_instance = error.is_error_instance;
    if ([...ENUMS.error_name, "RangeError", "ReferenceError", "ApiResponseError", "unknown"].includes(error.name)) evidence.name = error.name;
    if (Number.isInteger(error.status) && error.status >= 0 && error.status <= 599) evidence.status = error.status;
    if (typeof error.code === "string" && /^[a-z0-9_]{1,64}$/.test(error.code)) evidence.code = error.code;
    if (["empty", "failed_to_fetch", "data_manager_fallback", "other"].includes(error.message_kind)) evidence.message_kind = error.message_kind;
    evidence.stack_locations = (Array.isArray(error.stack_locations) ? error.stack_locations : []).slice(0, 3)
      .filter((item) => [item?.line, item?.column].every((n) => Number.isInteger(n) && n > 0 && n <= 999999999))
      .map(({ line, column }) => ({ line, column }));
    result[key] = evidence;
  }
  const transport = normalizeMutationTransport(value.transport);
  if (transport) result.transport = transport;
  return result;
}

export function createMutationTransportDiagnostic({ now = () => performance.now(), readAssetVersion = readLoadedDeploymentAssetVersion } = {}) {
  let started = null, headersAt = null, responseUrl = null;
  const state = { schema_version: 1, phase: "created", response_received: false,
    loaded_asset_version: safe(readAssetVersion) };
  const environment = () => {
    state.online = safe(() => globalThis.navigator?.onLine);
    state.visibility = safe(() => globalThis.document?.visibilityState);
    state.service_worker_controlled = safe(() => Boolean(globalThis.navigator?.serviceWorker?.controller));
  };
  const errorName = (error) => ENUMS.error_name.includes(safe(() => error?.name)) ? error.name : "other";
  return {
    request: () => safe(() => {
      started = now(); headersAt = null; responseUrl = null;
      // Each explicit request gets fresh evidence; never confuse a refresh with the mutation.
      for (const key of [...Object.keys(HEADERS), "http_status", "content_length", "content_kind", "headers_ms", "body_ms", "error_name", "redirected", "body_request_id"]) delete state[key];
      Object.assign(state, { phase: "request", response_received: false, started_at: new Date().toISOString() });
      environment();
    }),
    response: (response) => safe(() => {
      headersAt = now();
      responseUrl = safe(() => response.url);
      Object.assign(state, { phase: "read_body", response_received: true, http_status: response.status,
        headers_ms: started === null ? null : headersAt - started, redirected: response.redirected });
      for (const [key, header] of Object.entries(HEADERS)) state[key] = safe(() => response.headers.get(header));
      const contentType = safe(() => response.headers.get("content-type"), "") || "";
      state.content_kind = !contentType ? "missing" : /\bjson\b/i.test(contentType) ? "json"
        : /\bhtml\b/i.test(contentType) ? "html" : /^text\//i.test(contentType) ? "text" : "other";
      const length = safe(() => response.headers.get("content-length"));
      state.content_length = typeof length === "string" && /^\d{1,15}$/.test(length) ? Number(length) : null;
    }),
    json: (result, error, payload) => safe(() => {
      state.body_request_id = identifier(safe(() => payload?.request_id)) || identifier(safe(() => payload?.requestId));
      state.phase = result === "parsed" ? "parsed" : errorName(error) === "SyntaxError" ? "invalid_json" : "body_error";
      state.body_ms = headersAt === null ? null : now() - headersAt;
      if (error) state.error_name = errorName(error);
    }),
    fail: (error) => safe(() => {
      // A later display failure must not turn an already parsed response into a network failure.
      if (!state.response_received && started !== null) { state.phase = "network_error"; state.error_name = errorName(error); }
      environment();
    }),
    snapshot: () => safe(() => {
      // Read the completed mutation's own resource entry, without saving its URL
      // or attaching observers. This does not require performance collection.
      const resource = responseUrl && safe(() => performance.getEntriesByName(responseUrl).filter((entry) =>
        entry.initiatorType === "fetch" && entry.startTime >= started && entry.responseEnd >= headersAt).at(-1));
      return normalizeMutationTransport({ ...state, elapsed_ms: started === null ? null : now() - started,
        ...(resource ? { next_hop_protocol: resource.nextHopProtocol, transfer_size: resource.transferSize,
          encoded_body_size: resource.encodedBodySize, decoded_body_size: resource.decodedBodySize } : {}) });
    }),
  };
}
